import {
  CheckIcon,
  ChevronDownIcon,
  SearchLgIcon,
} from '@langchain/untitled-ui-icons';

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Button, Text } from '@/design-system';
import {
  useCurrentOrganization,
  useOrgWorkspaces,
  useOrganizationId,
} from '@/hooks/useSwr';
import { appOrganizationPath } from '@/utils/constants';
import { lowerCaseIncludes } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

import { OrgSettingsTabLink } from './OrgSettingsTabLink';
import { UpsellBadge } from './UpsellBadge';

export const WorkspacesNavItem: React.FC = () => {
  const navigate = useNavigate();
  const org = useCurrentOrganization();
  const isPersonal = org.data?.is_personal;
  const orgWorkspaces = useOrgWorkspaces();
  const workspaceId = useOrganizationId();

  const [workspaceSelectorOpen, setWorkspaceSelectorOpen] = useState(false);

  const location = useLocation();
  const [search, setSearch] = useState('');
  const isActive = location.pathname.startsWith(
    `/${appOrganizationPath}/${workspaceId}/settings/workspaces`
  );

  const activeWorkspace = orgWorkspaces.data?.find(
    (workspace) => workspace.id === workspaceId
  );
  return (
    <>
      <OrgSettingsTabLink
        disabled={isPersonal}
        to={`/${appOrganizationPath}/${workspaceId}/settings/workspaces/members`}
      >
        <div className="flex w-full flex-1 items-center justify-between">
          <Button
            variant="plain"
            color="secondary"
            size="md"
            disabled={isPersonal}
            className="flex h-full grow justify-start text-left"
          >
            <Text>Workspaces</Text>
            <div className="ml-auto">
              {isPersonal ? (
                <UpsellBadge
                  tooltipMsg={'Subscribe to Plus to access more workspaces'}
                  onClick={() => {
                    navigate(
                      `/${appOrganizationPath}/${workspaceId}/settings/payments`
                    );
                  }}
                  tier="PLUS"
                />
              ) : (
                orgWorkspaces.data?.length && (
                  <div className="mr-0.5 flex h-6 min-w-[24px] items-center justify-center rounded bg-slate-700/5 px-1 text-center text-xxs dark:bg-slate-50/5">
                    {orgWorkspaces.data.length}
                  </div>
                )
              )}
            </div>
          </Button>
        </div>
      </OrgSettingsTabLink>
      {activeWorkspace && isActive && (
        <div className="flex flex-col gap-2 border-l-2 border-l-gray-800 px-3 text-left font-semibold outline-none transition-all disabled:pointer-events-none disabled:opacity-50">
          <Popover
            open={workspaceSelectorOpen}
            onOpenChange={setWorkspaceSelectorOpen}
          >
            <PopoverTrigger asChild>
              <div
                key={activeWorkspace?.id}
                className={cn(
                  'flex w-full cursor-pointer gap-2 rounded-lg py-2 pl-3 pr-1 text-left font-normal text-primary outline-none transition-all disabled:pointer-events-none disabled:opacity-50',
                  'justify-between hover:bg-secondary'
                )}
              >
                {activeWorkspace?.display_name}
                <ChevronDownIcon className="size-4" />
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="max-h-[400px] overflow-y-auto p-2"
              align="start"
            >
              <div className="px-0">
                <div className="mb-2 flex items-center gap-2 rounded-md bg-secondary p-2">
                  <SearchLgIcon className="h-4 w-4" />
                  <input
                    className="w-full border-none bg-transparent p-0 text-sm outline-none"
                    type="text"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                {orgWorkspaces.data
                  ?.filter((i) => lowerCaseIncludes(i.display_name, search))
                  .map((workspace) => {
                    return (
                      <Link
                        key={workspace.id}
                        className={cn(
                          'text-md cursor-pointer select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-secondary',
                          'flex items-center justify-between gap-2 rounded-md text-sm'
                        )}
                        to={`/${appOrganizationPath}/${workspace.id}/settings/workspaces/members`}
                        onClick={() => setWorkspaceSelectorOpen(false)}
                      >
                        {workspace.display_name}
                        {workspace.id === workspaceId && (
                          <CheckIcon className="size-4" />
                        )}
                      </Link>
                    );
                  })}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </>
  );
};
